import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import {
  FunnelBar,
  PipelineCards,
  PipelineFunnelStage,
  PipelineHealth,
  PipelineRatio,
} from '@partner/feature-modules/dashboard/domain/model/pipeline-health.model';

/** 200 body of `GET api/dashboard/pipeline-health` (contract v1.0); instants are ISO-8601 UTC strings. */
export interface PipelineHealthResponse {
  asOfUtc: string;
  period: { from: IsoDate; to: IsoDate };
  funnel: FunnelBarResponse[];
  agingThresholdDays: number;
  cards: PipelineCardsResponse;
  /** Envelope symmetry with the list and snapshot responses; always null on 200. */
  validationMessage: string | null;
}

export interface FunnelBarResponse {
  stage: PipelineFunnelStage;
  count: number;
  percentOfStarted: number | null;
  agingCount: number | null;
}

export interface PipelineRatioResponse {
  percent: number | null;
  numerator: number;
  denominator: number;
}

export interface PipelineCardsResponse {
  blockedOnIncomplete: PipelineRatioResponse;
  approvedVsSubmitted: PipelineRatioResponse;
  fundedVsApproved: PipelineRatioResponse;
}

/**
 * `??` normalises a missing percentage to null and leaves a real 0 alone — null and 0 mean different
 * things here. `numerator` / `denominator` are not guarded: the contract omits no field, so a missing
 * one is a contract breach that must surface, not become "undefined of undefined deals".
 */
const adaptRatio = (ratio: PipelineRatioResponse): PipelineRatio => ({
  percent: ratio.percent ?? null,
  numerator: ratio.numerator,
  denominator: ratio.denominator,
});

const adaptFunnelBar = (bar: FunnelBarResponse): FunnelBar => ({
  stage: bar.stage,
  count: bar.count,
  percentOfStarted: bar.percentOfStarted ?? null,
  agingCount: bar.agingCount ?? null,
});

const adaptCards = (cards: PipelineCardsResponse): PipelineCards => ({
  blockedOnIncomplete: adaptRatio(cards.blockedOnIncomplete),
  approvedVsSubmitted: adaptRatio(cards.approvedVsSubmitted),
  fundedVsApproved: adaptRatio(cards.fundedVsApproved),
});

export const adaptPipelineHealth = (response: PipelineHealthResponse): PipelineHealth => ({
  asOfUtc: new Date(response.asOfUtc),
  period: { from: response.period.from, to: response.period.to },
  funnel: (response.funnel ?? []).map(adaptFunnelBar),
  agingThresholdDays: response.agingThresholdDays,
  cards: adaptCards(response.cards),
});
